import type { TileCache, TileMemoryBytes } from '@/plugins/tiles/runtime/TileCache'
import { TilesError } from '@/plugins/tiles/TilesError'

/** 多个 Record 只共享预算和淘汰顺序；内容、ID 和租约仍归各自缓存所有。 */
export class TileCacheBudget {
  private readonly _members = new Set<TileCache>()
  private _clock = 0
  private _maxBytes: number
  private _memoryLimit?: TileMemoryBytes
  private readonly _listeners = new Set<() => void>()
  private _notificationPending = false

  onCapacityChanged(listener: () => void): () => void {
    this._listeners.add(listener)
    return () => this._listeners.delete(listener)
  }

  notifyCapacityChanged(): void {
    if (this._notificationPending) return
    this._notificationPending = true
    queueMicrotask(() => {
      this._notificationPending = false
      for (const listener of this._listeners) listener()
    })
  }

  constructor(maxBytes: number) {
    this._maxBytes = this.validate(maxBytes)
  }

  configure(maxBytes: number): void {
    this._maxBytes = this.validate(maxBytes)
    this.ensureCapacity(0)
  }

  configureMemoryLimit(limit: TileMemoryBytes | false): void {
    if (
      limit !== false &&
      (!Number.isSafeInteger(limit.cpuBytes) ||
        limit.cpuBytes <= 0 ||
        !Number.isSafeInteger(limit.gpuBytes) ||
        limit.gpuBytes <= 0)
    )
      throw new TilesError({
        code: 'invalid-input',
        stage: 'runtime',
        message: 'Tiles CPU/GPU 额度必须为安全正整数',
      })
    this._memoryLimit = limit === false ? undefined : { ...limit }
    this.ensureCapacity(0)
  }

  getMemoryStats(): {
    readonly limit: TileMemoryBytes | undefined
    readonly committed: TileMemoryBytes
    readonly protected: TileMemoryBytes
    readonly reserved: TileMemoryBytes
  } {
    let cpuBytes = 0
    let gpuBytes = 0
    let protectedCpuBytes = 0
    let protectedGpuBytes = 0
    let reservedCpuBytes = 0
    let reservedGpuBytes = 0
    for (const member of this._members) {
      const stats = member.getMemoryStats()
      cpuBytes += stats.committed.cpuBytes
      gpuBytes += stats.committed.gpuBytes
      protectedCpuBytes += stats.protected.cpuBytes
      protectedGpuBytes += stats.protected.gpuBytes
      reservedCpuBytes += stats.reserved.cpuBytes
      reservedGpuBytes += stats.reserved.gpuBytes
    }
    return {
      limit: this._memoryLimit ? { ...this._memoryLimit } : undefined,
      committed: { cpuBytes, gpuBytes },
      protected: { cpuBytes: protectedCpuBytes, gpuBytes: protectedGpuBytes },
      reserved: { cpuBytes: reservedCpuBytes, gpuBytes: reservedGpuBytes },
    }
  }

  /** 在硬上限内主动回收长期未使用的共享缓存；保护租约与预约不受影响。 */
  trimToTarget(targetBytes: number, minIdleMs: number, maxEntries = 16, now = Date.now()): number {
    if (
      !Number.isSafeInteger(targetBytes) ||
      targetBytes < 0 ||
      !Number.isSafeInteger(minIdleMs) ||
      minIdleMs < 0 ||
      !Number.isSafeInteger(maxEntries) ||
      maxEntries < 1
    )
      throw new TilesError({
        code: 'invalid-input',
        stage: 'runtime',
        message: 'Tiles 缓存回收参数无效',
      })
    let freed = 0
    for (let count = 0; count < maxEntries && this.getStats().totalBytes > targetBytes; count++) {
      let oldest: { member: TileCache; id: string; bytes: number; used: number } | undefined
      for (const member of this._members) {
        for (const entry of member.entries()) {
          if (entry.bytes === 0 || entry.protected || now - entry.usedAt < minIdleMs) continue
          if (!oldest || entry.used < oldest.used)
            oldest = { member, id: entry.id, bytes: entry.bytes, used: entry.used }
        }
      }
      if (!oldest) break
      const before = this.getStats().totalBytes
      oldest.member.releaseUnprotected(oldest.id)
      const released = before - this.getStats().totalBytes
      if (released <= 0) break
      freed += released
    }
    return freed
  }

  getStats(): {
    maxBytes: number
    totalBytes: number
    protectedBytes: number
    reservedBytes: number
  } {
    let totalBytes = 0
    let protectedBytes = 0
    let reservedBytes = 0
    for (const member of this._members) {
      const stats = member.getStats()
      totalBytes += stats.totalBytes
      protectedBytes += stats.protectedBytes
      reservedBytes += stats.reservedBytes
    }
    return { maxBytes: this._maxBytes, totalBytes, protectedBytes, reservedBytes }
  }

  /** @internal 所有成员必须使用同一序号，局部时钟不能表达跨 Record 的 LRU。 */
  nextUse(): number {
    return ++this._clock
  }

  /** @internal */
  register(member: TileCache): void {
    this._members.add(member)
  }

  /** @internal */
  unregister(member: TileCache): void {
    this._members.delete(member)
  }

  /** @internal additional 是调用方预约重估或驻留替换的净增量。 */
  canEnsureCapacity(
    additional: number,
    requester: TileCache,
    excludeId: string,
    memory: TileMemoryBytes = { cpuBytes: 0, gpuBytes: 0 }
  ): boolean {
    let remaining = this.getStats().totalBytes + additional
    const stats = this.getMemoryStats().committed
    let cpuBytes = stats.cpuBytes + memory.cpuBytes
    let gpuBytes = stats.gpuBytes + memory.gpuBytes
    if (this.hasCapacity(remaining, cpuBytes, gpuBytes)) return true
    for (const member of this._members) {
      for (const entry of member.entries()) {
        if (!entry.protected && !(member === requester && entry.id === excludeId)) {
          remaining -= entry.bytes
          cpuBytes -= entry.cpuBytes
          gpuBytes -= entry.gpuBytes
        }
      }
    }
    return this.hasCapacity(remaining, cpuBytes, gpuBytes)
  }

  /** @internal 主动缩减预算尽力清理；新准入须先确认可满足，避免无收益淘汰。 */
  ensureCapacity(
    additional: number,
    requester?: TileCache,
    excludeId?: string,
    memory: TileMemoryBytes = { cpuBytes: 0, gpuBytes: 0 }
  ): boolean {
    if (
      requester &&
      excludeId !== undefined &&
      !this.canEnsureCapacity(additional, requester, excludeId, memory)
    )
      return false
    while (
      !this.hasCapacity(
        this.getStats().totalBytes + additional,
        this.getMemoryStats().committed.cpuBytes + memory.cpuBytes,
        this.getMemoryStats().committed.gpuBytes + memory.gpuBytes
      )
    ) {
      let oldest: { member: TileCache; used: number; bytes: number } | undefined
      for (const member of this._members) {
        for (const entry of member.entries()) {
          if (
            entry.bytes === 0 ||
            entry.protected ||
            (member === requester && entry.id === excludeId)
          )
            continue
          if (!oldest || entry.used < oldest.used)
            oldest = { member, used: entry.used, bytes: entry.bytes }
        }
      }
      if (!oldest) return false
      // 只释放全局最老的一项，再重新比较；不能批量淘汰一个成员的新内容。
      const released = oldest.member.evictUnprotected(
        oldest.bytes,
        oldest.member === requester ? excludeId : undefined
      )
      if (released <= 0) return false // 分批物理回收尚未完成，下一次容量通知再准入。
    }
    return true
  }

  private hasCapacity(bytes: number, cpuBytes: number, gpuBytes: number): boolean {
    return (
      bytes <= this._maxBytes &&
      (!this._memoryLimit ||
        (cpuBytes <= this._memoryLimit.cpuBytes && gpuBytes <= this._memoryLimit.gpuBytes))
    )
  }

  private validate(bytes: number): number {
    if (!Number.isFinite(bytes) || bytes <= 0)
      throw new TilesError({
        code: 'invalid-input',
        stage: 'runtime',
        message: 'Tiles 共享缓存预算必须有限且大于零',
      })
    return bytes
  }
}
